<script lang="ts">
  import { browser } from '$app/environment';
  import { onDestroy, onMount } from 'svelte';
  import viewport from '$lib/actions/useViewPort';

  import LikeOutlined from '$lib/assets/icons/LikeOutlined.svelte';
  import Liked from '$lib/assets/icons/Liked.svelte';
  import ShareIcon from '$lib/assets/icons/ShareIcon.svelte';
  import NewsMorphingWidget from '$lib/components/News/NewsMorphingWidget.svelte';
  import BackgroundComponent from '$lib/components/Shared/AdSlot/BackgroundComponent.svelte';
  import InterscrollarAdWrapper from '$lib/components/Shared/AdSlot/InterscrollarAdWrapper.svelte';
  import StickyScrollAd from '$lib/components/Shared/AdSlot/StickyScrollAd.svelte';
  import { AdConfig } from '$lib/components/Shared/AdSlot/config';
  import AdSlot from '$lib/components/Shared/AdSlot/index.svelte';
  import IntersectAnalytics from '$lib/components/Shared/IntersectAnalytics.svelte';
  import IntersectionObserver from '$lib/components/Shared/IntersectionObserver.svelte';
  import WidgetHeader from '$lib/components/Shared/WidgetHeader.svelte';
  import { SECONDS_IN_A_DAY } from '$lib/constants';
  import { OTHER_NEWS, READ_MORE } from '$lib/constants/text';
  import { NewsArticleAdUnits } from '$lib/services/ads/enums';
  import { sendAnalyticsEvent } from '$lib/services/analytics';
  import { getNewsFeed, getRelatedNews } from '$lib/services/http/cds/apis/news';
  import { isInterscrollerAdFilled, isInterscrollerAdVisible } from '$lib/store';
  import { EventNames, EventSectionName } from '$lib/types/analytics';
  import { NewsType, type NewsCDS } from '$lib/types/news';
  import { extractTextInsidePTags, handleSiteProvider, redirectTo, timeAgo } from '$lib/utils';
  import Logger from '$lib/utils/logger';
  import { createNewsDetailsUrl, getHeroImage } from '$lib/utils/news';
  import { PersistentStore } from '$lib/utils/persistent-store';
  import { onShare, sendContentSharedEvent } from '$lib/utils/share';
  import { writable } from 'svelte/store';
  import type { PageData } from './$types';
  import OutbrainAds from '$lib/components/Shared/AdSlot/OutbrainAds.svelte';

  export let data: PageData;

  const isVisible = writable(false);

  function handleUpdate(event) {
    isVisible.set(event.detail.isVisible);
  }

  const fetchRelatedNews = async (category: string, tags: string[] | null) => {
    try {
      let response: NewsCDS[] | undefined;

      // fetch related articles based on tags
      if (tags && tags.length > 0) response = await getRelatedNews(category, tags);

      // fetch related articles based on category if tags are not available
      if (!response || response.length < 4) response = await getRelatedNews(category);

      // fetch news feed if related articles are not available
      if (!response || response.length < 4) response = (await getNewsFeed(1, [category])).items;

      // filter out the current post from the related articles and limit to 4
      const posts = response?.filter((item) => item.sys.id !== data.post?.sys.id).slice(0, 4);
      return posts;
    } catch (error) {
      Logger.error(error);
    }
  };

  const handleClick = (publisherName: string) => {
    if (data.post?.showReadMore) {
      sendAnalyticsEvent(EventNames.BUTTON_CLICK, {
        publisher_name: publisherName,
        type: 'read_more'
      });
      window.location.href = data.post?.sourceUrl;
      return;
    }
  };

  const handleOnShare = () => {
    sendAnalyticsEvent(EventNames.SHARE_CLICK, { url: data.post?.sourceUrl });
    onShare({ url: createNewsDetailsUrl(category, postId) }, sendContentSharedEvent);
  };

  const handleLike = () => {
    if (isLiked) return;
    isLiked = true;
    PersistentStore.set(postId, 'LIKED', { expiryTime: 2 * SECONDS_IN_A_DAY });
    sendAnalyticsEvent(EventNames.LIKE_CLICKED, { url: data.post?.sourceUrl });
  };

  let intersecting = false;
  let relatedArticlesPromise: Promise<NewsCDS[] | undefined>;
  let isLiked = false;
  let isLoadingNextArticle = false;

  $: postId = data?.post?.sys?.id;
  $: category = data.post?.categories?.[0];
  $: tags = data.post?.tags;
  $: heroImage = data.post?.images ? getHeroImage(data.post) : '';
  $: showReadMore = data.post?.showReadMore;
  $: topNewsData = data.data?.topNews.filter((i) => i.type === NewsType.Image);
  $: articleIndex = topNewsData ? topNewsData.findIndex((i) => i.postId === postId) : -1;
  $: if (data) isLoadingNextArticle = false;

  // If intersection is met, fetch related articles
  $: {
    if (intersecting) {
      relatedArticlesPromise = fetchRelatedNews(category, tags);
    }
  }

  onMount(() => {
    if (PersistentStore.get(postId) === 'LIKED') {
      isLiked = true;
    }
    window?.OBR?.extern.reloadWidget();
  });

  onDestroy(() => {
    if (browser) {
      document.body.style.overflow = 'auto';
    }
  });

  $: {
    if (browser && window && data)
      window?.OBR?.extern?.refreshWidget('https://mysites.glance.com/');
  }

  const getArticle = (nudgeCategory: 'previous' | 'next') => {
    isLoadingNextArticle = true;
    const articleIndex = topNewsData ? topNewsData.findIndex((i) => i.postId === postId) : -1;

    switch (nudgeCategory) {
      // case 'prev':
      //   if (articleIndex && articleIndex !== -1) {
      //     // if (topNewsData && articleIndex === 0) {
      //     //   redirectTo(
      //     //     createNewsDetailsUrl(
      //     //       topNewsData[topNewsData.length - 1].beats[0],
      //     //       topNewsData[topNewsData.length - 1].postId
      //     //     )
      //     //   );
      //     //   return;
      //     // }
      //     redirectTo(
      //       createNewsDetailsUrl(
      //         data.data?.topNews[articleIndex - 1].beats[0]!,
      //         data.data?.topNews[articleIndex - 1].postId!
      //       )
      //     );
      //   }
      //   break;
      case 'next':
        if (articleIndex !== undefined && articleIndex !== -1 && topNewsData) {
          if (topNewsData && articleIndex === topNewsData.length - 1) {
            redirectTo(createNewsDetailsUrl(topNewsData[0].beats[0], topNewsData[0].postId));
            return;
          }
          redirectTo(
            createNewsDetailsUrl(
              topNewsData[articleIndex + 1].beats[0],
              topNewsData[articleIndex + 1].postId
            )
          );
        }
        break;
    }
    sendAnalyticsEvent(EventNames.BUTTON_CLICK, {
      cta: nudgeCategory,
      publisher: data.post?.outlet?.outletName,
      description: data.post?.headline
    });
  };
</script>

<svelte:head>
  <script src="https://widgets.outbrain.com/outbrain.js"></script>
</svelte:head>

<section>
  <!-- <div class="previous-article-nudge" on:click={() => getArticle('prev')}>left</div> -->
  {#if articleIndex !== -1}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="next-article-nudge"
      style="--background: {isLoadingNextArticle
        ? 'linear-gradient(128.35deg, #6c768b -10.06%, #424b5e 106.28%)'
        : 'linear-gradient(270deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.43) 96.27%)'}"
      on:click={() => getArticle('next')}
    >
      Next
    </div>
  {/if}
  <!-- ! figure out a better way to handle the case with nudges-->
  <div
    class="container"
    style={articleIndex !== -1 ? 'margin-top:-38px' : ''}
    class:wrapper={showReadMore}
  >
    <div
      class="scroll-overlay"
      style="background: var(--color-page-background); pointer-events: auto"
    >
      <img src={heroImage} alt={data.post?.headline || ''} class="hero-image" />
      <h2 class="title">{data.post?.headline}</h2>

      <div class="publishing-details-wrapper">
        <div class="publishing-details">
          <img
            src={data?.post?.outlet?.logo?.url || ''}
            alt={data.post?.outlet?.outletName || ''}
            class="publisher-logo"
          />
          <div class="divider">&#x2022;</div>
          {#if data.post?.publishedDate}
            <div class="published-time">{timeAgo(data.post?.publishedDate)}</div>
          {/if}
        </div>
        <div class="interaction-icons">
          <button class="like-icon-container" on:click={handleLike}>
            {#if isLiked}
              <Liked />
            {:else}
              <LikeOutlined fill="#ffffff" />
            {/if}
          </button>
          <button on:click={handleOnShare} class="share-icon-container">
            <ShareIcon />
          </button>
        </div>
      </div>
    </div>

    {#if !showReadMore}
      <div class="scroll-overlay" style="background: var(--color-page-background);">
        <div class="ad-slot-container">
          <AdSlot
            adType={handleSiteProvider() ? 'banner' : 'mrec'}
            adUnitName={handleSiteProvider()
              ? NewsArticleAdUnits.STICKY_BANNER
              : NewsArticleAdUnits.MREC_ATF}
            placementName={handleSiteProvider()
              ? AdConfig.banner.placementName
              : AdConfig.mrec.placementName}
          />
        </div>
      </div>
    {/if}
    <div class="news-details-content">
      {#if showReadMore}
        <p class="news-content">
          <!-- eslint-disable-next-line svelte/no-at-html-tags -->
          {@html data.post?.fullContent}
        </p>
        <div class="ad-slot-container">
          <AdSlot
            adType={handleSiteProvider() ? 'banner' : 'mrec'}
            adUnitName={handleSiteProvider()
              ? NewsArticleAdUnits.STICKY_BANNER
              : NewsArticleAdUnits.MREC_ATF}
            placementName={handleSiteProvider()
              ? AdConfig.banner.placementName
              : AdConfig.mrec.placementName}
          />
        </div>
      {:else}
        <div class="scroll-overlay">
          {#each data?.post.contentSegmentList as contentGroup, groupIndex}
            {#if groupIndex < 2 && contentGroup}
              <div
                class="news-content-group"
                style={groupIndex === 0 && $isInterscrollerAdFilled ? 'margin-bottom: 80vh;' : ''}
              >
                {#each extractTextInsidePTags(contentGroup) as content, index}
                  <p class="news-content-ap-news">{content}</p>
                {/each}
                {#if groupIndex === 0 && extractTextInsidePTags(contentGroup).length && $isInterscrollerAdFilled}
                  <div class="interscrollar-text">SCROLL TO CONTINUE READING</div>
                {/if}

                {#if groupIndex === 1}
                  <div class="ad-slot-container">
                    <AdSlot
                      adType="mrec"
                      adUnitName={NewsArticleAdUnits.MREC_MID_SCROLL}
                      placementName={AdConfig.mrec.placementName}
                    />
                  </div>
                {/if}
              </div>
            {:else}
              {#each extractTextInsidePTags(contentGroup) as content, index}
                <p class="news-content">{content}</p>
              {/each}
              <div class="ad-slot-container">
                <AdSlot
                  adType="mrec"
                  adUnitName={NewsArticleAdUnits.MREC_MID_SCROLL}
                  placementName={AdConfig.mrec.placementName}
                />
              </div>
            {/if}
          {/each}
        </div>

        <InterscrollarAdWrapper on:update={handleUpdate}>
          <BackgroundComponent slot="background" isVisible={$isVisible}>
            <div class="ad-slot-container">
              <AdSlot
                adType="interscrollar"
                adUnitName={NewsArticleAdUnits.L2_INTERSCROLLER}
                placementName={AdConfig.mrec.placementName}
              />
            </div>
          </BackgroundComponent>
        </InterscrollarAdWrapper>
      {/if}
    </div>

    {#if showReadMore}
      <div class="overlay">
        <button class="read-more-btn" on:click={() => handleClick(data.post.outlet.outletName)}
          >{READ_MORE}</button
        >
      </div>
      {#if !handleSiteProvider()}
        <!-- <AdSlot
        adType="mrec"
        adUnitName={NewsArticleAdUnits.MREC_BTF}
        placementName={AdConfig.mrec.placementName}
        /> -->
      {/if}
    {/if}
    {#if !showReadMore}
      <!-- <AdSlot
          adType="mrec"
          adUnitName={NewsArticleAdUnits.MREC_BTF}
          placementName={AdConfig.mrec.placementName}
          /> -->
    {/if}
    <!-- <div class="separator" /> -->

    <IntersectionObserver bind:intersecting once />
    <IntersectAnalytics
      eventName={EventNames.SECTION_VIEW}
      sectionName={EventSectionName.OTHER_NEWS}
    >
      <div
        class="scroll-overlay"
        style="background: var(--color-page-background); pointer-events: auto"
      >
        <NewsMorphingWidget
          promise={relatedArticlesPromise}
          sectionName={EventSectionName.OTHER_NEWS}
        >
          <svelte:fragment slot="header">
            <WidgetHeader title={OTHER_NEWS} />
          </svelte:fragment>
        </NewsMorphingWidget>

        <!-- <div class="ad-slot-container" style="margin-bottom:60px">
          <AdSlot
            adType="mrec"
            adUnitName={NewsArticleAdUnits.MREC_END}
            placementName={AdConfig.mrec.placementName}
          />
        </div>
      </div> -->
      </div>
    </IntersectAnalytics>
    <div
      class="scroll-overlay"
      style="background: var(--color-page-background); pointer-events: auto"
    >
      <IntersectAnalytics sectionName={EventSectionName.OUTBRAIN_FEED}>
        <OutbrainAds widgetId={'MB_5'} />
      </IntersectAnalytics>
    </div>

    <StickyScrollAd>
      <div class="ad-slot-container">
        <AdSlot
          adType="banner"
          adUnitName={NewsArticleAdUnits.L2_STICKY_BANNER}
          placementName={AdConfig.banner.placementName}
        />
      </div>
    </StickyScrollAd>
  </div>
</section>

<style lang="scss">
  .ad-slot-container {
    position: relative;
    pointer-events: auto;
    width: 100%;
    height: 100%;
  }
  .container {
    padding: 15px 12px;
    position: relative;
    z-index: 1;
    pointer-events: none;
  }

  .wrapper {
    .overlay {
      pointer-events: auto;
      display: flex;
      justify-content: center;
      align-items: flex-end;
      padding-bottom: 12px;
    }

    .read-more-btn {
      // ! revert for light theme and think of a way to set global variables for gradients
      // background: linear-gradient(180deg, #ff503d 0%, #e43234 99.86%);
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.9) 0%,
        rgba(255, 255, 255, 0.56) 100%
      );
      border-radius: 28px;
      border: none;
      // change this to global tomorrow
      color: #000;
      padding: 16px;
      font-size: var(--font-size-base);
      letter-spacing: 0.21px;
    }
  }

  .title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    line-height: 140%;
    margin: 16px 0;
    color: var(--color-title);
  }

  .hero-image {
    border-radius: 16px;
    width: 100%;
    height: 100%;
    object-fit: cover;
    aspect-ratio: 3/1.5;
  }

  .publishing-details-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    // margin-bottom: 12px;
    margin-bottom: -1px;
  }

  .publishing-details {
    display: flex;
    align-items: center;
  }

  .publisher-logo {
    width: 24px;
    height: 24px;
    object-fit: cover;
    border-radius: 12px;
  }

  .divider {
    margin: 0 8px;
    color: var(--color-text);
  }

  .published-time {
    text-transform: uppercase;
    color: #ffffff99;
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-semibold);
    line-height: 127%;
  }

  .share-icon-container {
    margin-right: 6px;
    display: flex;
    border: none;
    background: transparent;
  }

  .news-details-content {
    position: relative;
    margin-top: -1px;
    :global(br) {
      display: none;
    }
  }

  // .separator {
  //   width: 100%;
  //   height: 1px;
  //   background-color: var(--color-divider);
  //   opacity: 0.1;
  //   border-radius: 2px;
  //   margin: 24px 0 12px 0;
  // }

  .interaction-icons {
    display: flex;
  }

  .like-icon-container {
    border: none;
    background-color: transparent;
    margin-right: 16px;
  }

  .news-content {
    font-size: var(--font-size-base);
    line-height: 150%;
    color: var(--color-title);
    margin-bottom: 12px;
    line-height: 22.5px;
  }
  .news-content-ap-news {
    font-size: var(--font-size-base);
    line-height: 150%;
    color: var(--color-title);
    margin-bottom: 12px;
  }

  // .previous-article-nudge {
  //   position: sticky;
  //   top: 422px;
  //   left: 0;
  //   width: fit-content;
  //   background: linear-gradient(
  //     90deg,
  //     rgba(255, 255, 255, 0.7) 0%,
  //     rgba(255, 255, 255, 0.43) 96.27%
  //   );
  //   stroke-width: 1px;
  //   stroke: #fff;
  //   filter: drop-shadow(0px 10px 14px rgba(0, 0, 0, 0.45));
  //   backdrop-filter: blur(25.899999618530273px);
  //   padding: 6px 12px;
  //   border-top-right-radius: 16px;
  //   border-bottom-right-radius: 16px;
  //   overflow: hidden;
  // }

  .next-article-nudge {
    box-sizing: border-box;
    opacity: 1;
    position: sticky;
    z-index: 3;
    top: 54%;
    right: 0;
    margin-left: auto;
    width: fit-content;
    background: var(--background);
    stroke-width: 1px;
    stroke: #fff;
    filter: drop-shadow(0px 10px 14px rgba(0, 0, 0, 0.45));
    backdrop-filter: blur(25.899999618530273px);
    padding: 6px 12px;
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
    color: #141923;
    text-align: center;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: 26px; /* 216.667% */
    letter-spacing: 0.21px;
  }

  .scroll-overlay {
    position: relative;
    z-index: 2;
    box-sizing: border-box;
    // pointer-events: none;
  }

  .news-content-group {
    position: relative; /* Relative positioning for z-index to work */
    pointer-events: auto;
    background: var(--color-page-background);
    color: var(--color-title);
    padding: 5px;
    border-radius: 5px 5px 0 0;
    // pointer-events: none;
    z-index: 2;
  }
  .interscrollar-text {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--color-white);
    color: var(--color-black);
    border-radius: 0 0 5px 5px;
    padding: 2px 0;
  }
</style>
